import React, { useEffect, useState, useCallback } from 'react';
import { getClientsForCalibration, saveCalibration, contractTypeFromRaw, CHANNELS, CHANNEL_LABELS, type ClientCalib, type CalibrationData, type Channel } from '../../lib/refonteApi';
import ContractBadge from '../../components/refonte/ContractBadge';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

type PM = 'fixed' | 'fixed_plus_variable' | 'gtm' | 'days';
const EMPTY: CalibrationData = {
  channel: 'cold_call', pricing_model: 'fixed', fixed_price: null, price_per_qualified_rdv: null, contestation_window_days: 7,
  sectors: [], zones: [], target_functions: [], exclusions: [], size_min: null, size_max: null, client_signed_at: null, version: 0,
};
const toArr = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

const CalibragesPage: React.FC = () => {
  const [clients, setClients] = useState<ClientCalib[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<CalibrationData>(EMPTY);
  const [sign, setSign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setClients(await getClientsForCalibration()); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const sel = clients.find(c => c.id === selId) || null;
  const select = (c: ClientCalib) => {
    setSelId(c.id); setMsg(null); setErr(null); setSign(false);
    setForm(c.calibration ? { ...c.calibration } : { ...EMPTY });
  };

  const save = async () => {
    if (!sel) return;
    setSaving(true); setErr(null); setMsg(null);
    try {
      await saveCalibration(sel.id, form, sign);
      setMsg('Calibrage enregistré.');
      await load();
    } catch (e: any) { setErr(e.message); }
    setSaving(false);
  };

  const filtered = clients.filter(c =>
    (!onlyActive || c.statut === 'actif') &&
    (!q || c.name.toLowerCase().includes(q.toLowerCase())));

  const set = (patch: Partial<CalibrationData>) => setForm(f => ({ ...f, ...patch }));

  return (
    <div>
      <h1 className="text-[21px] font-bold tracking-tight">Calibrages · contrats</h1>
      <p className="text-dim text-[13px] mb-5">Définir le contrat de chaque client : formule, prix, critères ICP. C'est ce qui pilote la facturation et les statuts RDV.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4 items-start">
        {/* Liste clients */}
        <div className="bg-panel border border-line rounded-card p-4 lg:h-[74vh] flex flex-col">
          <div className="flex gap-2 mb-3 shrink-0">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un client…" className="flex-1 rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px]" />
            <button onClick={() => setOnlyActive(v => !v)} className={`text-[11.5px] font-semibold px-2.5 rounded-md border ${onlyActive ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim'}`}>Actifs</button>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-line -mx-1 px-1">
            {loading ? <SkeletonRows count={8} /> : filtered.map(c => (
              <button key={c.id} onClick={() => select(c)}
                className={`w-full text-left py-2.5 px-2 -mx-2 rounded-lg flex items-center gap-2 hover:bg-panel-2 ${selId === c.id ? 'bg-panel-2 ring-1 ring-line-2' : ''}`}>
                <span className="flex-1 min-w-0 truncate text-[13px] font-semibold">{c.name}</span>
                {c.statut !== 'actif' && <span className="font-mono text-[9px] text-faint">inactif</span>}
                {c.calibration ? <ContractBadge contractType={contractTypeFromRaw(c.calibration.pricing_model)} /> : <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn">à définir</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Éditeur */}
        <div className="bg-panel border border-line rounded-card p-5 lg:h-[74vh] overflow-y-auto">
          {!sel ? <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un client" hint="Choisis un client à gauche pour définir/éditer son contrat." /></div> : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[16px] font-bold">{sel.name}</div>
                  <div className="font-mono text-[10px] text-faint">{sel.calibration ? `calibrage v${sel.calibration.version} · ${sel.calibration.client_signed_at ? 'signé client' : 'non signé'}` : 'aucun calibrage'}</div>
                </div>
                <ContractBadge contractType={contractTypeFromRaw(form.pricing_model)} />
              </div>

              {msg && <div className="mb-3 text-[12px] text-st-ok bg-st-ok/10 rounded px-3 py-2">{msg}</div>}
              {err && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 rounded px-3 py-2">{err}</div>}

              {/* Pricing */}
              <div className="mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">Canal, formule & prix</div>
                <div className="flex flex-wrap gap-3 items-end">
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Canal (offre)</span>
                    <select value={form.channel ?? 'cold_call'} onChange={e => set({ channel: e.target.value as Channel })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                      {CHANNELS.map(c => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Formule</span>
                    <select value={form.pricing_model ?? 'fixed'} onChange={e => set({ pricing_model: e.target.value as PM })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                      <option value="fixed">Forfait</option><option value="fixed_plus_variable">Hybride</option><option value="gtm">GTM</option><option value="days">Jours</option>
                    </select></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Prix fixe €/mois</span>
                    <input type="number" value={form.fixed_price ?? ''} onChange={e => set({ fixed_price: e.target.value ? Number(e.target.value) : null })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-32" /></label>
                  {form.pricing_model === 'fixed_plus_variable' && (
                    <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Prix / RDV validé €</span>
                      <input type="number" value={form.price_per_qualified_rdv ?? ''} onChange={e => set({ price_per_qualified_rdv: e.target.value ? Number(e.target.value) : null })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-28" /></label>
                  )}
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Contestation (j ouvrés)</span>
                    <input type="number" value={form.contestation_window_days ?? 7} onChange={e => set({ contestation_window_days: Number(e.target.value) })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-24" /></label>
                </div>
              </div>

              {/* Critères ICP */}
              <div className="mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">Critères ICP (séparés par des virgules)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Secteurs</span>
                    <input value={form.sectors.join(', ')} onChange={e => set({ sectors: toArr(e.target.value) })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]" /></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Zones</span>
                    <input value={form.zones.join(', ')} onChange={e => set({ zones: toArr(e.target.value) })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]" /></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Fonctions cibles</span>
                    <input value={form.target_functions.join(', ')} onChange={e => set({ target_functions: toArr(e.target.value) })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]" /></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Exclusions</span>
                    <input value={form.exclusions.join(', ')} onChange={e => set({ exclusions: toArr(e.target.value) })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]" /></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Taille min (salariés)</span>
                    <input type="number" value={form.size_min ?? ''} onChange={e => set({ size_min: e.target.value ? Number(e.target.value) : null })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-28" /></label>
                  <label className="flex flex-col gap-1"><span className="text-[11px] text-dim">Taille max (salariés)</span>
                    <input type="number" value={form.size_max ?? ''} onChange={e => set({ size_max: e.target.value ? Number(e.target.value) : null })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-28" /></label>
                </div>
              </div>

              <label className="flex items-center gap-2 mb-4 text-[12.5px] text-dim">
                <input type="checkbox" checked={sign} onChange={e => setSign(e.target.checked)} className="accent-hook-rose w-4 h-4" />
                Marquer comme signé par le client (nouvelle version)
              </label>

              <button disabled={saving} onClick={save} className="text-[13px] font-semibold px-4 py-2 rounded-md bg-hook-rose text-white disabled:opacity-40">
                {saving ? 'Enregistrement…' : 'Enregistrer le calibrage'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalibragesPage;
